import { useEffect, useState, useCallback } from "react";
import { Eye, UserPlus, Inbox, Nfc, QrCode, MousePointerClick, Users } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import api, { fileUrl } from "@/lib/api";
import { StatCard, PeriodSelect, Spinner } from "@/components/dashboard/DashBits";

function ProfileCard({ profile }) {
  const d = profile.data || {};
  const primary = d.primary_color || "#06B6D4";
  const fullName = `${d.first_name || ""} ${d.last_name || ""}`.trim() || profile.code;
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d14] p-5 flex items-center gap-4" data-testid="stats-profile-card">
      <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-white/10 flex items-center justify-center shrink-0" style={{ background: `${primary}22` }}>
        {d.profile_photo_url
          ? <img src={fileUrl(d.profile_photo_url)} alt={fullName} className="w-full h-full object-cover" />
          : <span className="font-display font-bold text-xl" style={{ color: primary }}>{(d.first_name?.[0] || "") + (d.last_name?.[0] || "")}</span>}
      </div>
      <div className="min-w-0">
        <p className="font-display font-semibold text-white text-lg truncate">{fullName}</p>
        {d.job_title && <p className="text-sm text-zinc-400 truncate">{d.job_title}</p>}
        {d.company_name && <p className="text-xs text-zinc-500 truncate">{d.company_name}</p>}
      </div>
    </div>
  );
}

export default function Stats() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user.role === "superadmin";
  const [period, setPeriod] = useState("30d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selected, setSelected] = useState(""); // "" = all profiles

  useEffect(() => {
    if (isAdmin) return;
    api.get("/company/profiles").then(({ data }) => setProfiles(data)).catch(() => setProfiles([]));
  }, [isAdmin]);

  const load = useCallback(() => {
    setData(null);
    const base = isAdmin ? "/admin/stats" : "/company/stats";
    let url = `${base}?period=${period}`;
    if (period === "custom" && custom.start && custom.end) url += `&start=${custom.start}&end=${custom.end}`;
    if (!isAdmin && selected) url += `&profile_id=${encodeURIComponent(selected)}`;
    api.get(url).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [isAdmin, period, custom, selected]);

  useEffect(() => { load(); }, [load]);

  const tt = data?.totals || {};
  const selectedProfile = profiles.find((p) => p.id === selected);

  return (
    <div>
      <h1 className="font-display font-bold text-3xl text-white">{t("dash.statsTitle")}</h1>
      <p className="mt-1 text-zinc-400">{t("dash.statsSub")}</p>

      {!isAdmin && (
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="text-sm text-zinc-400 flex items-center gap-2 shrink-0">
            <Users className="w-4 h-4 text-cyan-400" /> {t("dash.statsFor")}
          </label>
          <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="stats-profile-select"
            className="bg-[#0d0d14] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40 min-w-[220px]">
            <option value="">{t("dash.allProfiles")}</option>
            {profiles.map((p) => {
              const d = p.data || {};
              const name = `${d.first_name || ""} ${d.last_name || ""}`.trim() || p.code;
              return <option key={p.id} value={p.id}>{name}{p.active ? "" : " •"}</option>;
            })}
          </select>
        </div>
      )}

      {!isAdmin && selectedProfile && (
        <div className="mt-4">
          <ProfileCard profile={selectedProfile} />
          {!selectedProfile.active && <p className="mt-2 text-xs text-amber-400">{t("dash.inactiveProfile")}</p>}
        </div>
      )}

      <div className="mt-6"><PeriodSelect value={period} onChange={setPeriod} custom={custom} setCustom={setCustom} /></div>

      {data === null ? <Spinner /> : data === false ? <p className="text-zinc-500 mt-8">{t("dash.loadErr")}</p> : (
        <>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard icon={Eye} label={t("dash.totalOpens")} value={tt.views || 0} accent="#3b82f6" testid="stats-views" />
            <StatCard icon={Nfc} label={t("dash.viaNfc")} value={tt.nfc || 0} accent="#06b6d4" testid="stats-nfc" />
            <StatCard icon={QrCode} label={t("dash.viaQr")} value={tt.qr || 0} accent="#8b5cf6" testid="stats-qr" />
            <StatCard icon={MousePointerClick} label={t("dash.directAccess")} value={tt.direct || 0} accent="#22d3ee" testid="stats-direct" />
            <StatCard icon={UserPlus} label={t("dash.addsContacts")} value={tt.add_contact || 0} accent="#10b981" testid="stats-adds" />
            <StatCard icon={Inbox} label={t("dash.coordRequests")} value={tt.contact_request || 0} accent="#f59e0b" testid="stats-requests" />
          </div>

          <div className="mt-6 rounded-2xl border border-white/10 bg-[#0d0d14] p-6">
            <h3 className="font-display font-semibold text-white mb-4">{t("dash.evolution")}</h3>
            {(data.series || []).length === 0 ? (
              <p className="text-zinc-500 text-sm py-12 text-center">{t("dash.noDataPeriod")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
                  <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#14141f", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                  <Line type="monotone" dataKey="views" name={t("dash.seriesViews")} stroke="#06b6d4" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="add_contact" name={t("dash.seriesAdds")} stroke="#10b981" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="contact_request" name={t("dash.seriesReq")} stroke="#f59e0b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}
